import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { Editor, isActive } from '@tiptap/react';
import Bulletlist from './BulletList';
import { useParams } from 'react-router-dom';
import { socket } from '../../../Socket';
import './snippetcontainer.scss';
import * as monaco from 'monaco-editor';
import Snippet from './Snippet';
import { WorkspaceContext } from '../WorkspaceContext';
import SnipSettingPopup from './SnipSettingPopup';
import api from '../../../api';
import { getSnippetIcon } from '../../../shared/utils/getFileIconAndExtensionHelper';
import { getSnippetExtension } from '../../../shared/utils/getFileIconAndExtensionHelper';
import FilePreview from '../preview/FilePreview';
import { getFileRefIcon } from '../../../shared/utils/getFileIconAndExtensionHelper';
import Download from '../../../shared/components/svgIcons/Download';
import { downloadFile } from '../sidebar/DownloadHelper';
import { normalizeMime } from '../../../shared/utils/normalizeMime';
import useCloseOnClickAway from '../../../shared/hooks/UseCloseOnClickAway';
import FileSettingPopup from './FileSettingPopup';
import { useAppUser } from '../../../context/UserContext/UserContext';
import { downloadSnippet } from '../sidebar/DownloadHelper';
import OptionsIcon from '../../../shared/components/svgIcons/OptionsIcon';
import { useCodeRunner } from '../../coderunner/CodeRunnerContext';
import type { OpenTab } from '../WorkspaceContext';
import { findFolderInTree } from '../../../shared/utils/folderTree';
import EditorErrorBoundary from '../../../shared/components/EditorErrorBoundary';

export type Snippet = {
    _id: string;
    snippetName: string;
    snippetType: string,
    snippetLanguage?: string,
    folderParent: string | null,
    snipText: string,
    createdBy: string,
    snipPermissions: string,
    snippetSetting: string,
    updatedAt?: string;
};

function shortenFileTabName(fileName: string, maxBaseLength = 15) {
    if (!fileName) return "Untitled";

    const lastDotIndex = fileName.lastIndexOf(".");

    // No extension found
    if (lastDotIndex === -1) {
        return fileName.length > maxBaseLength
            ? `${fileName.slice(0, maxBaseLength)}...`
            : fileName;
    }

    const baseName = fileName.slice(0, lastDotIndex);
    const extension = fileName.slice(lastDotIndex); // includes the dot, like ".pdf"

    const shortBaseName =
        baseName.length > maxBaseLength
            ? `${baseName.slice(0, maxBaseLength)}...`
            : baseName;

    return `${shortBaseName}${extension}`;
}

// The name actually shown on an open tab — used both to render the tab and,
// via tabNameCounts below, to detect when two open tabs would show the same
// label (so their parent folder can be shown to disambiguate them, mirroring
// VS Code's behavior for duplicate-named open editors).
function getTabDisplayName(tab: OpenTab): string {
    if (tab.resourceType === "snippet") {
        return tab.data.snippetType === "code"
            ? `${tab.data.snippetName}.${getSnippetExtension(tab.data.snippetLanguage || "txt")}`
            : `${tab.data.snippetName}.txt`;
    }

    return shortenFileTabName(tab.data.name, 15);
}



const SnippetContainer: React.FC = () => {

    const API = process.env.REACT_APP_API_URL;


    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error("Workspace must be wrapped in WorkspaceContext.Provider");
    const { ctheme, membersList, setLimitMessage, setLimitPopup, openSnippets, setOpenSnippets, activeTerminal, activeSnippet, activeSnippetId, setActiveSnippetId, Snippets, memberDetails, roomDetails, clientId, globalNewFilePopup, setGlobalNewFilePopup, globalNewSnipPopup, setGlobalNewSnipPopup, openTabs, setOpenTabs, activeFileId, setActiveFileId, activeFile, Files, Folders, roomStorage, setRoomStorage, setDownloadFileErrorPopup, setUiMode, setSbMode } = ctx;
    const { activeCode, setActiveLanguage } = useCodeRunner();
    const { user: appUser } = useAppUser();
    const hasOwnerBypass = appUser?.plan === 'flashroom-owner';

    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isBullet, setIsBullet] = useState(false);
    const textEditorsRef = useRef<Record<string, Editor | null>>({});
    const codeEditorsRef = useRef<Record<string, monaco.editor.IStandaloneCodeEditor | null>>({});
    const codeTextRef = useRef<Record<string, string>>({});
    const richTextRef = useRef<Record<string, string>>({});
    const codeSubRef = useRef<Record<string, monaco.IDisposable | null>>({});
    const textSubRef = useRef<Record<string, (() => void) | null>>({})
    // const [activeSnippet, setActiveSnippet] = useState(snippets[0]);
    // const activeSnippetId = activeSnippet?.id ?? null;

    const snippetMapRef = useRef<Map<string, string>>(new Map());


    const tiptapTimersRef = useRef<Map<string, any>>(new Map());

    // ensure we only wire each editor once
    const wiredMonaco = useRef<WeakSet<monaco.editor.IStandaloneCodeEditor>>(new WeakSet());
    const wiredTipTap = useRef<WeakSet<Editor>>(new WeakSet());


    const [permissionsPopup, setPermissionsPopup] = useState(false)

    const togglePermissionsPopup = (e: React.MouseEvent) => {
        e.stopPropagation();
        setPermissionsPopup(prev => !prev);
    };

    // The permissions button lives beside the toolbar controls on mobile,
    // but stays at the bottom of the editor on desktop — matches the
    // sidebar's mobile breakpoint (SideBar.tsx) so both switch together.
    const [isMobileToolbar, setIsMobileToolbar] = useState(() => window.matchMedia('(max-width: 600px)').matches);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 600px)');
        const handleChange = (e: MediaQueryListEvent) => setIsMobileToolbar(e.matches);
        mq.addEventListener('change', handleChange);
        return () => mq.removeEventListener('change', handleChange);
    }, []);

    // const [clientIdRef, setclientIdRef] = useState<string | null>('');


    const snippetCanEditRef = useRef<Record<string, boolean>>({});
    const snippetCanChangeSettingsRef = useRef<Record<string, boolean>>({});
    const snippetCanCreateRef = useRef<Record<string, boolean>>({});


    const focusedSnippetIdRef = useRef<string | null>(null);
    const clientPatchSeqRef = useRef(0);
    const seenRemotePatchIdsRef = useRef<Set<string>>(new Set());
    const pendingSnapshotRef = useRef<Record<string, { text: string; updatedAt?: string }>>({});


    const [snipSettingPopup, setSnipSettingPopup] = useState(false);
    const [fileSettingPopup, setFileSettingPopup] = useState(false);

    const fileSettingClick = () => {

        setGlobalNewFilePopup(false)
        setGlobalNewSnipPopup(false)
        setSnipSettingPopup(false)

        setFileSettingPopup(true)
    }

    const snipSettingClick = () => {
        if (!activeSnippetId) return

        setGlobalNewFilePopup(false)
        setGlobalNewSnipPopup(false)
        setFileSettingPopup(false)

        setSnipSettingPopup(true)
    }

    const snipSettingCancelClick = () => {
        setSnipSettingPopup(false);
    }

    useEffect(() => {
        setSnipSettingPopup(false);
    }, [activeSnippetId, activeFileId]);


    const activeTextEditor =
        activeSnippetId != null ? textEditorsRef.current[activeSnippetId] ?? null : null;

    const activeCodeEditor =
        activeSnippetId != null ? codeEditorsRef.current[activeSnippetId] ?? null : null


    const [textCopyNoty, showTextCopyNoty] = useState(false);

    const permPopupRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!permissionsPopup) return;

        function handleClickOutside(event: MouseEvent) {
            if (
                permPopupRef.current &&
                event.target instanceof Node &&
                !permPopupRef.current.contains(event.target)
            ) {
                setPermissionsPopup(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside, true);
        };
    }, [permissionsPopup]);


    const popupRef = useRef<HTMLDivElement | null>(null);

    useCloseOnClickAway({
        targetRef: popupRef,
        onClose: () => setSnipSettingPopup(false),
    });

    const filePopupRef = useRef<HTMLDivElement | null>(null);

    useCloseOnClickAway({
        targetRef: filePopupRef,
        onClose: () => setFileSettingPopup(false),
    });






    const snapshotRequestedAtRef = useRef<Record<string, number>>({});
    const lastLocalEditAtRef = useRef<Record<string, number>>({});

    const didAutoPickRef = useRef(false);

    useEffect(() => {
        if (didAutoPickRef.current) return;
        if (!openSnippets.length) return;

        // only auto-pick if nothing is selected
        if (!activeSnippetId) {
            didAutoPickRef.current = true;
            const first = openSnippets[0];
            setActiveSnippetId(first._id);
            setActiveLanguage(first.snippetLanguage || "");
        }
    }, [openSnippets, activeSnippetId]);

    const { roomId } = useParams();


    const applyingRemoteCodeRef = useRef<Set<string>>(new Set());

    // useEffect(() => {
    //     const m = snippetMapRef.current;
    //     m.clear();
    //     for (const s of Snippets) {
    //         m.set(s._id, s.snipText ?? "");
    //     }
    // }, [Snippets]);

    useEffect(() => {
        const m = snippetMapRef.current;

        for (const s of Snippets) {
            const prev = m.get(s._id);

            // Only update cache if we actually received text.
            // If snipText is undefined/null, keep what we already had.
            if (typeof s.snipText === "string") {
                m.set(s._id, s.snipText);
            } else if (prev === undefined) {
                // first time seeing it, ok to initialize to empty
                m.set(s._id, "");
            }
        }
    }, [Snippets]);

    const ROOM_STORAGE_LIMIT_BYTES = 100 * 1024 * 1024;

    const roomStorageRef = useRef(roomStorage ?? 0);
    const snippetBaseBytesRef = useRef<Record<string, number>>({});
    const snippetPendingDeltaBytesRef = useRef<Record<string, number>>({});

    useEffect(() => {
        roomStorageRef.current = roomStorage ?? 0;
    }, [roomStorage]);

    const getByteSize = (text: string) => new Blob([text]).size;

    const getPendingStorageDeltaExcept = (snippetId: string) => {
        return Object.entries(snippetPendingDeltaBytesRef.current).reduce(
            (total, [id, delta]) => id === snippetId ? total : total + delta,
            0
        );
    };

    const canSetSnippetText = (snippetId: string, nextText: string) => {
        if (hasOwnerBypass) return true;

        const baseBytes = snippetBaseBytesRef.current[snippetId] ?? 0;
        const nextBytes = getByteSize(nextText);
        const nextSnippetDelta = nextBytes - baseBytes;

        const projectedStorage =
            roomStorageRef.current +
            getPendingStorageDeltaExcept(snippetId) +
            nextSnippetDelta;

        return projectedStorage <= ROOM_STORAGE_LIMIT_BYTES;
    };

    const acceptSnippetTextForStorage = (snippetId: string, nextText: string) => {
        const baseBytes = snippetBaseBytesRef.current[snippetId] ?? 0;
        snippetPendingDeltaBytesRef.current[snippetId] = getByteSize(nextText) - baseBytes;
    };

    const showStorageLimitReached = () => {
        setLimitMessage("Storage limit reached");
        setLimitPopup(true);
    };




    function wireMonacoDeltaSender(id: string, ed: monaco.editor.IStandaloneCodeEditor) {
        const model = ed.getModel();
        if (!model) return;

        let t: any = null;
        const FLUSH_MS = 120;

        const flush = () => {
            t = null;
            const m = ed.getModel();
            if (!m || !roomId) return;

            if (!snippetCanEditRef.current[id]) return;
            if (applyingRemoteCodeRef.current.has(id)) return;

            const val = m.getValue();
            const cached = snippetMapRef.current.get(id) ?? "";
            const effectiveVal = val;

            if (effectiveVal.trim().length === 0 && cached.trim().length > 0) {
                return;
            }

            snippetMapRef.current.set(id, effectiveVal);
            queueAutosave(id, effectiveVal);
        };

        // ✅ define "flush now" ONCE
        monacoFlushRef.current[id] = () => {
            if (t) clearTimeout(t);
            try {
                flush();
            } finally {
                resolveFlush(id);
            }
        };

        let revertingLocalLimitChange = false;
        let lastAcceptedValue = model.getValue();

        snippetBaseBytesRef.current[id] ??= getByteSize(lastAcceptedValue);


        const emitMonacoPatch = (changes: CodeChange[], text: string) => {
            if (!roomId || changes.length === 0) return;

            const patchId = `${clientId || socket.id || "client"}:${Date.now()}:${clientPatchSeqRef.current++}`;
            const payload = {
                roomId,
                snippetId: id,
                patchId,
                senderId: socket.id,
                senderClientId: clientId,
                changes,
                text,
                updatedAt: lastSeenUpdatedAtRef.current[id],
            };

            let acked = false;
            const send = () => {
                if (acked) return;
                socket.emit("snippet:patch", payload, (ok?: boolean) => {
                    if (ok) acked = true;
                });
            };

            send();
            window.setTimeout(send, 350);
            window.setTimeout(send, 1200);
        };

        const sub = model.onDidChangeContent((event) => {
            if (revertingLocalLimitChange) return;
            if (applyingRemoteCodeRef.current.has(id)) return;

            const nextValue = model.getValue();
            const deltaBytes = getByteSize(nextValue) - getByteSize(lastAcceptedValue);

            if (!canSetSnippetText(id, nextValue)) {
                revertingLocalLimitChange = true;

                try {
                    model.setValue(lastAcceptedValue);
                    showStorageLimitReached();
                } finally {
                    setTimeout(() => {
                        revertingLocalLimitChange = false;
                    }, 0);
                }

                return;
            }

            lastAcceptedValue = nextValue;
            acceptSnippetTextForStorage(id, nextValue);


            lastLocalTypeAtRef.current[id] = Date.now();
            lastLocalEditAtRef.current[id] = Date.now();

            if (t) clearTimeout(t);

            snippetMapRef.current.set(id, nextValue);
            emitMonacoPatch(event.changes.map((change) => ({
                range: {
                    startLineNumber: change.range.startLineNumber,
                    startColumn: change.range.startColumn,
                    endLineNumber: change.range.endLineNumber,
                    endColumn: change.range.endColumn,
                },
                rangeOffset: change.rangeOffset,
                rangeLength: change.rangeLength,
                text: change.text,
            })), nextValue);

            markFlushPending(id);
            t = setTimeout(() => {
                try {
                    flush();
                } finally {
                    resolveFlush(id);
                }
            }, FLUSH_MS);
        });


        codeSubRef.current[id] = sub;
    }

    type ActiveResourceId = {
        id: string;
        resourceType: "snippet" | "file";
    };

    const activeTab = openTabs.find(
        (tab) => tab.data._id === activeFileId?.id
    );

    const iconFile =
        activeTab?.resourceType === "file"
            ? getFileRefIcon(activeTab.data.name)
            : activeTab?.resourceType === "snippet" &&
                activeTab.data.snippetType === "code" &&
                activeTab.data.snippetLanguage
                ? getSnippetIcon(activeTab.data.snippetLanguage) + '.png'
                : null;



    const isActiveAudioFile =
        activeFile?.resourceType === 'file' && activeFile.data.mime?.startsWith('audio/')

    const isActiveVideoFile = activeFile?.resourceType === 'file' && activeFile.data.mime?.startsWith('video/')



    //end newest

    const lastLocalTypeAtRef = useRef<Record<string, number>>({});

    // queue to buffer remote edits while user is typing fast
    const remoteQueueRef = useRef<Record<string, RemoteItem[]>>({});

    // suppress local emits while we apply a remote change

    // threshold to consider the user "idle"
    const IDLE_MS = 250;
    type CodeChange = {
        range: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number };
        rangeOffset?: number;
        rangeLength?: number;
        text: string;
    };

    type RemoteItem = { patchId?: string; changes?: CodeChange[]; text?: string; updatedAt?: string };

    useEffect(() => {
        // attach a listener per mounted editor (idempotent in your wiring)
        Object.entries(codeEditorsRef.current).forEach(([id, ed]) => {
            if (!ed) return;
            const d = ed.onDidChangeModelContent(() => {
                // only update on LOCAL edits (skip if we're in remote-apply)
                if (!applyingRemoteCodeRef.current.has(id)) {
                    lastLocalTypeAtRef.current[id] = Date.now();
                }
            });
            // store/cleanup if you want; Monaco will drop it with the model
        });
    }, []);





    const DEBOUNCE_MS = 250;

    const flushPromiseRef = useRef<Record<string, Promise<void> | null>>({});
    const flushResolveRef = useRef<Record<string, (() => void) | null>>({});

    const markFlushPending = (id: string) => {
        if (flushPromiseRef.current[id]) return; // already pending
        flushPromiseRef.current[id] = new Promise<void>((res) => {
            flushResolveRef.current[id] = res;
        });
    };

    const resolveFlush = (id: string) => {
        flushResolveRef.current[id]?.();
        flushResolveRef.current[id] = null;
        flushPromiseRef.current[id] = null;
    };

    const handleEditorUpdate = useCallback((
        payload: { editor?: Editor; editorRef?: monaco.editor.IStandaloneCodeEditor },
        id: string
    ) => {
        if (payload.editor && textEditorsRef.current[id] !== payload.editor) {
            textSubRef.current[id]?.();

            const ted = payload.editor;
            textEditorsRef.current[id] = ted;

            const cached = snippetMapRef.current.get(id);
            if (cached !== undefined) {
                try { ted.commands.setContent(cached as any, { emitUpdate: false }); }
                catch { }
            }
            snippetMapRef.current.set(id, ted.getHTML());

            if (!wiredTipTap.current.has(ted)) {
                const schedule = (key: string, fn: () => void) => {
                    const timers = tiptapTimersRef.current;
                    if (timers.has(key)) clearTimeout(timers.get(key));
                    const t = setTimeout(fn, DEBOUNCE_MS);
                    timers.set(key, t);
                };

                const doSendAndSave = () => {
                    if (applyingRemoteTipTapRef.current.has(id)) return;

                    if (!snippetCanEditRef.current[id]) return;
                    if (!roomId) return;

                    const htmlNow = ted.getHTML();
                    snippetMapRef.current.set(id, htmlNow);

                    socket.emit("snippet:patch", {
                        roomId,
                        snippetId: id,
                        senderId: socket.id,
                        senderClientId: clientId,
                        text: htmlNow,
                        updatedAt: lastSeenUpdatedAtRef.current[id]
                    });

                    queueAutosave(id, htmlNow);
                };

                tiptapFlushRef.current[id] = () => {
                    const key = `rt:${id}`;
                    const timers = tiptapTimersRef.current;
                    if (timers.has(key)) {
                        clearTimeout(timers.get(key));
                        timers.delete(key);
                    }
                    try {
                        doSendAndSave();
                    } finally {
                        resolveFlush(id);
                    }
                };

                let lastAcceptedHTML = ted.getHTML();
                snippetBaseBytesRef.current[id] ??= getByteSize(lastAcceptedHTML);


                const onUpdate = () => {
                    if (applyingRemoteTipTapRef.current.has(id)) return;

                    const nextHTML = ted.getHTML();
                    const deltaBytes = getByteSize(nextHTML) - getByteSize(lastAcceptedHTML);

                    if (!canSetSnippetText(id, nextHTML)) {
                        applyingRemoteTipTapRef.current.add(id);

                        try {
                            ted.commands.setContent(lastAcceptedHTML, { emitUpdate: false });
                            showStorageLimitReached();
                        } finally {
                            setTimeout(() => {
                                applyingRemoteTipTapRef.current.delete(id);
                            }, 0);
                        }

                        return;
                    }

                    lastAcceptedHTML = nextHTML;
                    acceptSnippetTextForStorage(id, nextHTML);


                    const now = Date.now();
                    lastLocalTypeAtTipRef.current[id] = now;
                    lastLocalEditAtRef.current[id] = now;

                    if (!snippetCanEditRef.current[id]) return;

                    markFlushPending(id);
                    schedule(`rt:${id}`, () => {
                        try { doSendAndSave(); }
                        finally { resolveFlush(id); }
                    });
                };


                const onFocus = () => { focusedSnippetIdRef.current = id; };

                const onBlur = () => {
                    if (focusedSnippetIdRef.current === id) focusedSnippetIdRef.current = null;

                    const pending = tiptapRemotePendingRef.current[id];
                    if (pending?.text?.length) {
                        tiptapRemotePendingRef.current[id] = null;

                        applyingRemoteTipTapRef.current.add(id);
                        try {
                            ted.commands.setContent(pending.text, { emitUpdate: false });
                            snippetMapRef.current.set(id, ted.getHTML());

                            if (pending.updatedAt) {
                                lastSeenUpdatedAtRef.current[id] = pending.updatedAt;
                            }
                        } finally {
                            setTimeout(() => applyingRemoteTipTapRef.current.delete(id), 0);
                        }
                    }
                };

                ted.on("update", onUpdate);
                ted.on("focus", onFocus);
                ted.on("blur", onBlur);

                // cleanup
                textSubRef.current[id] = () => {
                    ted.off("update", onUpdate);
                    ted.off("focus", onFocus);
                    ted.off("blur", onBlur);

                    tiptapFlushRef.current[id] = null;
                };

                wiredTipTap.current.add(ted);
            }



            return;
        }

        if (payload.editorRef && codeEditorsRef.current[id] !== payload.editorRef) {
            codeSubRef.current[id]?.dispose();

            const ed = payload.editorRef;
            codeEditorsRef.current[id] = ed;

            const model = ed.getModel();
            const cached = snippetMapRef.current.get(id);

            if (model) {
                const currentModelValue = model.getValue();
                const cachedIsString = typeof cached === "string";
                const cachedIsEmpty = !cachedIsString || cached.trim().length === 0;
                const modelHasContent = currentModelValue.trim().length > 0;

                if (cachedIsString) {
                    // Do not let an empty cached value wipe a model that already has content
                    if (cachedIsEmpty && modelHasContent) {
                        snippetMapRef.current.set(id, currentModelValue);
                    } else {
                        applyingRemoteCodeRef.current.add(id);
                        try {
                            model.setValue(cached);
                        } finally {
                            setTimeout(() => applyingRemoteCodeRef.current.delete(id), 0);
                        }
                    }
                } else {
                    snippetMapRef.current.set(id, currentModelValue);
                }
            }

            const focusDisposables: monaco.IDisposable[] = [];
            focusDisposables.push(
                ed.onDidFocusEditorText(() => { focusedSnippetIdRef.current = id; }),
                ed.onDidBlurEditorText(() => {
                    if (focusedSnippetIdRef.current === id) focusedSnippetIdRef.current = null;

                    const pending = pendingSnapshotRef.current[id];
                    const currentVal = ed.getModel()?.getValue() ?? "";

                    if (pending && typeof pending.text === "string") {
                        const incoming = pending.text;
                        const incomingUpdatedAt = pending.updatedAt;

                        if (incomingUpdatedAt && !isIncomingNewer(id, incomingUpdatedAt)) {
                            delete pendingSnapshotRef.current[id];
                            return;
                        }

                        if (currentVal.trim().length > 0 && incoming.trim().length === 0) {
                            delete pendingSnapshotRef.current[id];
                            return;
                        }

                        applyingRemoteCodeRef.current.add(id);
                        try {
                            applyRemoteCodeFullReplace(ed, incoming);
                            const m = ed.getModel();
                            if (m) snippetMapRef.current.set(id, m.getValue());

                            if (incomingUpdatedAt) {
                                lastSeenUpdatedAtRef.current[id] = incomingUpdatedAt;
                            }
                        } finally {
                            delete pendingSnapshotRef.current[id];
                            setTimeout(() => applyingRemoteCodeRef.current.delete(id), 0);
                        }
                    }
                })
            );

            if (!wiredMonaco.current.has(ed)) {
                wireMonacoDeltaSender(id, ed);
                wiredMonaco.current.add(ed);
            }





            const modelDispose = model?.onWillDispose?.(() => {
                focusDisposables.forEach(d => d.dispose());

                // ✅ clear flush hooks + any pending timers/promise state
                monacoFlushRef.current[id] = null;
                resolveFlush(id);              // optional but helpful if something was pending
            });
            if (modelDispose) codeSubRef.current[id] = modelDispose;
            return;
        }
    }, [roomId]);


    const hydratedCodeRef = useRef<Set<string>>(new Set());


    function applyRemoteCodeFullReplace(
        ed: monaco.editor.IStandaloneCodeEditor,
        nextText: string
    ) {
        const model = ed.getModel();
        if (!model) return;

        const fullRange = model.getFullModelRange();

        // 1. Capture current readOnly
        const prevOptions = ed.getRawOptions();
        const wasReadOnly = !!prevOptions.readOnly;

        // 2. Temporarily allow edits
        if (wasReadOnly) {
            ed.updateOptions({ readOnly: false });
        }

        try {
            ed.executeEdits("remote", [{
                range: fullRange,
                text: nextText,
            }]);
            ed.pushUndoStop();
        } finally {
            // 3. Restore original readOnly
            if (wasReadOnly) {
                ed.updateOptions({ readOnly: true });
            }
        }
    }

    function trimSeenPatchIds() {
        const seen = seenRemotePatchIdsRef.current;
        if (seen.size <= 500) return;

        const keep = Array.from(seen).slice(-250);
        seen.clear();
        keep.forEach((id) => seen.add(id));
    }

    function transformOffsetThroughChange(offset: number, change: CodeChange) {
        const start = change.rangeOffset;
        const length = change.rangeLength;

        if (typeof start !== "number" || typeof length !== "number") return offset;

        const end = start + length;
        const insertedLength = change.text.length;

        if (offset <= start) return offset;
        if (offset >= end) return offset + insertedLength - length;
        return start + insertedLength;
    }

    function applyRemoteCodeChanges(
        ed: monaco.editor.IStandaloneCodeEditor,
        changes: CodeChange[]
    ) {
        const model = ed.getModel();
        if (!model || changes.length === 0) return;

        const selections = ed.getSelections() ?? [];
        const transformedSelectionOffsets = selections.map((selection) => {
            let startOffset = model.getOffsetAt(selection.getStartPosition());
            let endOffset = model.getOffsetAt(selection.getEndPosition());

            for (const change of changes) {
                startOffset = transformOffsetThroughChange(startOffset, change);
                endOffset = transformOffsetThroughChange(endOffset, change);
            }

            return {
                direction: selection.getDirection(),
                startOffset: Math.max(0, startOffset),
                endOffset: Math.max(0, endOffset),
            };
        });

        const prevOptions = ed.getRawOptions();
        const wasReadOnly = !!prevOptions.readOnly;

        if (wasReadOnly) {
            ed.updateOptions({ readOnly: false });
        }

        try {
            model.applyEdits(changes.map((change) => ({
                range: new monaco.Range(
                    change.range.startLineNumber,
                    change.range.startColumn,
                    change.range.endLineNumber,
                    change.range.endColumn
                ),
                text: change.text,
                forceMoveMarkers: true,
            })));

            const modelLength = model.getValueLength();
            const transformedSelections = transformedSelectionOffsets.map((selection) => {
                const start = model.getPositionAt(Math.min(modelLength, selection.startOffset));
                const end = model.getPositionAt(Math.min(modelLength, selection.endOffset));

                return selection.direction === monaco.SelectionDirection.RTL
                    ? new monaco.Selection(end.lineNumber, end.column, start.lineNumber, start.column)
                    : new monaco.Selection(start.lineNumber, start.column, end.lineNumber, end.column);
            });

            ed.setSelections(transformedSelections);
        } finally {
            if (wasReadOnly) {
                ed.updateOptions({ readOnly: true });
            }
        }
    }


    function applyRemoteText(snippetId: string, nextText: string) {
        const normalized = nextText.replace(/\r\n/g, "\n");
        snippetMapRef.current.set(snippetId, normalized);

        const ed = codeEditorsRef.current[snippetId];

        if (ed) {
            applyingRemoteCodeRef.current.add(snippetId);
            try {
                applyRemoteCodeFullReplace(ed, normalized);
                const model = ed.getModel();
                if (model) snippetMapRef.current.set(snippetId, model.getValue());
            } finally {
                setTimeout(
                    () => applyingRemoteCodeRef.current.delete(snippetId),
                    0
                );
            }
            return;
        }


    }

    const applyingRemoteTipTapRef = useRef<Set<string>>(new Set());

    const saveTimersRef = useRef<Record<string, number>>({});
    const lastSeenUpdatedAtRef = useRef<Record<string, string>>({});

    const DEBOUNCE_SAVE_MS = 500;

    useEffect(() => {
        for (const s of Snippets) {
            if (s._id && s.updatedAt) lastSeenUpdatedAtRef.current[s._id] = s.updatedAt;
        }
    }, [Snippets]);

    const queueAutosave = useCallback((snippetId: string, text: string) => {
        const prev = saveTimersRef.current[snippetId];
        if (prev) clearTimeout(prev as unknown as number);

        saveTimersRef.current[snippetId] = window.setTimeout(async () => {
            try {
                const baseUpdatedAt = lastSeenUpdatedAtRef.current[snippetId] || '';
                const { data } = await api.patch(`/snippets/updatesnippet/${roomId}/${snippetId}`, {
                    snipText: text,
                    baseUpdatedAt,
                    clientId: clientId,
                });

                const savedUpdatedAt = data?.updatedAt ?? data?.snippet?.updatedAt;
                if (savedUpdatedAt) lastSeenUpdatedAtRef.current[snippetId] = savedUpdatedAt;

                // The server skips its usual broadcast for text-only saves (that's
                // what stopped the autosave feedback loop), so this client has to
                // apply its own storage delta locally instead of waiting for one.
                // Other room members get the same delta via snippet:storage-delta.
                if (typeof data?.storageDelta === 'number' && data.storageDelta !== 0) {
                    setRoomStorage(prev => prev + data.storageDelta);
                }
            } catch (err: any) {

                if (err?.response?.status === 409) {
                    const { snipText: serverText, updatedAt } = err.response.data || {};
                    if (typeof serverText === 'string') applyRemoteText(snippetId, serverText);
                    if (updatedAt) lastSeenUpdatedAtRef.current[snippetId] = updatedAt;
                } else {
                }
            }
        }, DEBOUNCE_SAVE_MS) as unknown as number;
    }, [API, setRoomStorage]);





    const snippetById = useMemo(
        () => new Map(Snippets.map(s => [s._id, s])),
        [Snippets]
    );

    // How many open tabs currently display each name — used to only show a
    // disambiguating folder label on tabs whose name actually collides with
    // another open tab's, same as VS Code only annotates duplicate editors.
    const tabNameCounts = useMemo(() => {
        const counts = new Map<string, number>();
        for (const tab of openTabs) {
            const name = getTabDisplayName(tab);
            counts.set(name, (counts.get(name) ?? 0) + 1);
        }
        return counts;
    }, [openTabs]);

    // The actual <Snippet>/<FilePreview> instances (below, in #snippet-holder)
    // are stacked with `.snippet { position: absolute; inset: 0 }` — every one
    // renders in the exact same spot regardless of DOM order, with only the
    // active one visible. So unlike the tab bar, their render order has zero
    // visual effect. Rendering them in `openTabs`' order anyway meant dragging
    // a tab to reorder it *also* reparented every open Monaco editor's DOM
    // node for no visible benefit — which is what was crashing editors on
    // reorder (moving a mounted @monaco-editor/react instance's container
    // triggers its automaticLayout ResizeObserver mid-move, racing Monaco's
    // own internal disposal and throwing "InstantiationService has been
    // disposed"). Sorting by id here keeps this list's order stable across a
    // drag, so reordering tabs only ever touches the tab bar.
    const stableOrderedTabs = useMemo(
        () => [...openTabs].sort((a, b) => a.data._id.localeCompare(b.data._id)),
        [openTabs]
    );

    const isRoomCreator = roomDetails?.creatorClientId === clientId;



    const isAdmin = memberDetails?.role === "admin";

    const canEditSnippet = useCallback(
        (snippetId: string) => {
            const s = snippetById.get(snippetId);
            if (!s) return false;

            const creatorDetails = membersList.find(m => m.clientId === s.createdBy);
            const creatorRole = creatorDetails?.role;

            const isOwner = s.createdBy === clientId;

            if (s.snipPermissions === "edit") return true;

            if (isOwner) return true;

            if (isRoomCreator) return true;

            if (isAdmin) {
                if (creatorRole === "owner") return false;
                if (creatorRole === "admin") return false;
                return true;
            }

            return false;
        },
        [snippetById, membersList, clientId, isAdmin, isRoomCreator]
    );

    const canChangeSnipSettings = useCallback(
        (snippetId: string) => {
            const s = snippetById.get(snippetId);
            if (!s) return false;

            const creatorDetails = membersList.find(m => m.clientId === s.createdBy);
            const creatorRole = creatorDetails?.role;

            const isOwner = s.createdBy === clientId;

            if (isRoomCreator) return true;
            if (isOwner) return true;

            if (isAdmin) {
                if (creatorRole === "admin") return false;
                if (creatorRole === "owner") return false;
                return true;
            }

            return false;
        },
        [snippetById, membersList, clientId, isAdmin, isRoomCreator]
    );


    useEffect(() => {
        for (const s of Snippets) {
            snippetCanEditRef.current[s._id] = canEditSnippet(s._id);
            snippetCanChangeSettingsRef.current[s._id] = canChangeSnipSettings(s._id);
        }

    }, [Snippets, canEditSnippet, canChangeSnipSettings]);





    useEffect(() => {
        if (!activeSnippetId) return;
        const ed = textEditorsRef.current[activeSnippetId];
        // false = don't emit an 'update' event. TipTap's setEditable emits one by
        // default, which is indistinguishable from a real edit to the onUpdate
        // handler below — every re-run of this effect (e.g. a Snippets refresh
        // recomputing canEditSnippet) was triggering a save of unchanged content.
        if (ed) ed.setEditable(canEditSnippet(activeSnippetId), false);
    }, [activeSnippetId, canEditSnippet]);

    //new
    useEffect(() => {
        if (!roomId) return;

        const joinAll = () => {
            const joined = new Set<string>();

            for (const s of openSnippets) {
                const snipId = s._id;
                if (!snipId) continue;
                socket.emit("snippet:join", { roomId, snippetId: snipId });
                joined.add(String(snipId));
            }

            return joined;
        };

        // join now if connected
        let joinedNow: Set<string> | null = null;
        if (socket.connected) joinedNow = joinAll();

        // rejoin after connect/reconnect
        const onConnect = () => { joinAll(); };
        socket.on("connect", onConnect);

        return () => {
            socket.off("connect", onConnect);

            // only leave rooms we know we joined in this lifecycle
            if (joinedNow) {
                joinedNow.forEach((snipId) => {
                    socket.emit("snippet:leave", { roomId, snippetId: snipId });
                });
            }
        };
    }, [roomId, openSnippets]);


    const isIncomingNewer = useCallback((snippetId: string, incomingUpdatedAt?: string) => {
        if (!incomingUpdatedAt) return false;

        const current = lastSeenUpdatedAtRef.current[snippetId];
        if (!current) return true;

        return new Date(incomingUpdatedAt).getTime() >= new Date(current).getTime();
    }, []);

    //receive patches and apply to the correct editor
    useEffect(() => {
        function scheduleApplyWhenIdle(id: string, delayMs = IDLE_MS): void {
            const tick: () => void = () => {
                const ed = codeEditorsRef.current[id];
                if (!ed) return;

                const queued = remoteQueueRef.current[id];
                if (!queued || queued.length === 0) return;

                const hasLiveChanges = queued.some((item) => Array.isArray(item.changes) && item.changes.length > 0);
                const last = lastLocalTypeAtRef.current[id] ?? 0;
                const idle = Date.now() - last >= IDLE_MS;

                if (!hasLiveChanges && !idle) {
                    window.setTimeout(tick, 60);
                    return;
                }

                const changeItems = queued.filter((item) => Array.isArray(item.changes) && item.changes.length > 0);
                const textItems = queued.filter((item) => typeof item.text === "string");
                remoteQueueRef.current[id] = [];

                if (changeItems.length > 0) {
                    applyingRemoteCodeRef.current.add(id);
                    try {
                        for (const item of changeItems) {
                            if (!item.changes?.length) continue;
                            applyRemoteCodeChanges(ed, item.changes);
                        }
                        const model = ed.getModel();
                        if (model) snippetMapRef.current.set(id, model.getValue());
                    } finally {
                        window.setTimeout(() => applyingRemoteCodeRef.current.delete(id), 0);
                    }
                    return;
                }

                let nextItem: RemoteItem | null = null;

                for (const item of textItems) {
                    if (!nextItem) {
                        nextItem = item;
                        continue;
                    }

                    const nextTime = nextItem.updatedAt ? new Date(nextItem.updatedAt).getTime() : -1;
                    const itemTime = item.updatedAt ? new Date(item.updatedAt).getTime() : -1;

                    if (itemTime >= nextTime) nextItem = item;
                }

                if (!nextItem || typeof nextItem.text !== "string") return;

                const nextText = nextItem.text;
                const incomingUpdatedAt = nextItem.updatedAt;
                const currentVal = ed.getModel()?.getValue() ?? "";

                if (incomingUpdatedAt && !isIncomingNewer(id, incomingUpdatedAt)) return;

                applyingRemoteCodeRef.current.add(id);
                try {
                    if (currentVal !== nextText) applyRemoteCodeFullReplace(ed, nextText);
                    const model = ed.getModel();
                    if (model) snippetMapRef.current.set(id, model.getValue());

                    if (incomingUpdatedAt) {
                        lastSeenUpdatedAtRef.current[id] = incomingUpdatedAt;
                    }
                } finally {
                    window.setTimeout(() => applyingRemoteCodeRef.current.delete(id), 0);
                }
            };

            window.setTimeout(tick, delayMs);
        }
        const onApply = (p: {
            snippetId?: string;
            patchId?: string;
            changes?: CodeChange[];
            text?: string;
            updatedAt?: string;
            senderId?: string;
            senderClientId?: string;
        }) => {
            const id = p?.snippetId;
            if (!id) return;
            const hasChanges = Array.isArray(p.changes) && p.changes.length > 0;
            const hasText = typeof p.text === "string";
            if (!hasChanges && !hasText) return;

            if (p.senderClientId && p.senderClientId === clientId) {
                return;
            }

            if (p.patchId) {
                if (seenRemotePatchIdsRef.current.has(p.patchId)) return;
                seenRemotePatchIdsRef.current.add(p.patchId);
                trimSeenPatchIds();
            }

            const incoming = p.text ?? "";
            const incomingUpdatedAt = p.updatedAt;
            const incomingIsEmpty = incoming.trim().length === 0;

            const ted = textEditorsRef.current[id];
            const ed = codeEditorsRef.current[id];

            const cachedValue = snippetMapRef.current.get(id) ?? "";

            // stale version guard
            if (!hasChanges && incomingUpdatedAt && !isIncomingNewer(id, incomingUpdatedAt)) {
                return;
            }

            //empty update bug patch #1
            //Commenting this out for now as it could be the main culprit
            // if (incomingIsEmpty && hasMeaningfulLocalContent) return;

            // Monaco
            if (ed) {
                if (!remoteQueueRef.current[id]) remoteQueueRef.current[id] = [];
                remoteQueueRef.current[id].push({
                    patchId: p.patchId,
                    changes: p.changes,
                    text: hasChanges ? undefined : incoming,
                    updatedAt: incomingUpdatedAt,
                });
                scheduleApplyWhenIdle(id, hasChanges ? 0 : IDLE_MS);
                return;
            }

            // TipTap
            if (ted && hasText) {
                tiptapRemotePendingRef.current[id] = {
                    text: incoming,
                    updatedAt: incomingUpdatedAt,
                };
                if (incomingUpdatedAt) {
                    lastSeenUpdatedAtRef.current[id] = incomingUpdatedAt;
                }
                scheduleTipTapApplyWhenIdle(id);
                return;
            }

            // No mounted editor
            if (!hasText) return;

            const cachedHasContent = cachedValue.trim().length > 0;

            if (incomingIsEmpty && cachedHasContent) {
                return;
            }

            snippetMapRef.current.set(id, incoming);

            if (incomingUpdatedAt) {
                lastSeenUpdatedAtRef.current[id] = incomingUpdatedAt;
            }

            pendingSnapshotRef.current[id] = {
                text: incoming,
                updatedAt: incomingUpdatedAt,
            };
        };

        socket.on("snippet:patch", onApply);
        return () => {
            socket.off("snippet:patch", onApply);
        };
    }, [clientId, isIncomingNewer]);


    const lastLocalTypeAtTipRef = useRef<Record<string, number>>({});
    const tiptapRemotePendingRef = useRef<Record<string, { text: string; updatedAt?: string } | null>>({});
    const TIP_IDLE_MS = 250;

    function scheduleTipTapApplyWhenIdle(id: string) {
        const tick: () => void = () => {
            const ted = textEditorsRef.current[id];
            if (!ted) return;

            // ✅ do NOT clobber while user is in the editor
            if (focusedSnippetIdRef.current === id) {
                setTimeout(tick, 120);
                return;
            }

            const last = lastLocalTypeAtTipRef.current[id] ?? 0;
            const idle = Date.now() - last >= TIP_IDLE_MS;
            if (!idle) return void setTimeout(tick, 60);

            const pending = tiptapRemotePendingRef.current[id];
            if (!pending?.text) return;

            // optional stale guard
            if (pending.updatedAt && !isIncomingNewer(id, pending.updatedAt)) {
                tiptapRemotePendingRef.current[id] = null;
                return;
            }

            // (optional) skip if already same
            if (pending.text === ted.getHTML()) {
                tiptapRemotePendingRef.current[id] = null;
                return;
            }

            tiptapRemotePendingRef.current[id] = null;

            applyingRemoteTipTapRef.current.add(id);
            try {
                ted.commands.setContent(pending.text, { emitUpdate: false });
                snippetMapRef.current.set(id, ted.getHTML());

                if (pending.updatedAt) {
                    lastSeenUpdatedAtRef.current[id] = pending.updatedAt;
                }
            } finally {
                setTimeout(() => applyingRemoteTipTapRef.current.delete(id), 0);
            }
        };

        setTimeout(tick, TIP_IDLE_MS);
    }





    const activeSnippetFile =
        activeFile?.resourceType === "snippet" ? activeFile.data : null;



    useEffect(() => {
        const onBroadcastSnapshotRequest = (p: { requestId?: string; roomId?: string; snippetId?: string }) => {
            const { requestId, snippetId } = p || {};
            if (!requestId || !snippetId) return;

            const snip = snippetById.get(snippetId);
            if (!snip) return;

            // Try Monaco first
            const codeEd = codeEditorsRef.current[snippetId];
            if (codeEd) {
                const model = codeEd.getModel();
                const text =
                    model?.getValue() ??
                    snippetMapRef.current.get(snippetId) ??
                    snip.snipText ??
                    "";

                if (text && text.trim().length > 0) {
                    socket.emit("snippet:sendSnapshot", {
                        requestId,
                        snippetId,
                        text,
                        updatedAt: lastSeenUpdatedAtRef.current[snippetId],

                    });
                    return;
                }
                // if it's empty, let someone else / DB answer
            }

            // Then TipTap
            const ted = textEditorsRef.current[snippetId];
            if (ted) {
                const html =
                    ted.getHTML() ??
                    snippetMapRef.current.get(snippetId) ??
                    snip.snipText ??
                    "";

                const stripped = html.replace(/<[^>]*>/g, "").trim();
                if (stripped.length > 0) {
                    socket.emit("snippet:sendSnapshot", {
                        requestId,
                        snippetId,
                        text: html,
                        updatedAt: lastSeenUpdatedAtRef.current[snippetId],
                    });
                    return;
                }
                // empty → let DB answer
            }

            // Finally, fall back to whatever we last cached / DB text
            const cached =
                snippetMapRef.current.get(snippetId) ??
                snip.snipText ??
                "";

            if (cached && cached.trim().length > 0) {
                socket.emit("snippet:sendSnapshot", {
                    requestId,
                    snippetId,
                    text: cached,
                    updatedAt: lastSeenUpdatedAtRef.current[snippetId],
                });
            }
            // if even cached is empty, we just don't answer → DB fallback
        };

        socket.on("snippet:broadcastSnapshotRequest", onBroadcastSnapshotRequest);
        return () => {
            socket.off("snippet:broadcastSnapshotRequest", onBroadcastSnapshotRequest);
        };
    }, [snippetById]);


    useEffect(() => {

    }, [activeSnippetId])





    const requestSnippetSnapshot = useCallback(
        (snippetId: string) => {
            if (!roomId) return;

            const snip = snippetById.get(snippetId);
            if (!snip) return;

            socket.emit(
                "snippet:requestSnapshot",
                { roomId, snippetId },
                (payload?: { status?: string; text?: string; updatedAt?: string }) => {
                    if (!payload || payload.status !== "ok" || typeof payload.text !== "string") {
                        return;
                    }



                    const incoming = payload.text ?? "";
                    const incomingUpdatedAt = payload.updatedAt;

                    const requestedAt = snapshotRequestedAtRef.current[snippetId] ?? 0;
                    const lastLocalEditAt = lastLocalEditAtRef.current[snippetId] ?? 0;



                    if (!isIncomingNewer(snippetId, payload.updatedAt)) {
                        return;
                    }

                    // Current mounted editors
                    const codeEd = codeEditorsRef.current[snippetId];
                    const ted = textEditorsRef.current[snippetId];

                    // Current local values
                    const localCodeValue = codeEd?.getModel()?.getValue() ?? "";
                    const localHtmlValue = ted?.getHTML() ?? "";
                    const cachedValue = snippetMapRef.current.get(snippetId) ?? "";
                    const dbValue = snip.snipText ?? "";

                    const localTextStripped = localHtmlValue.replace(/<[^>]*>/g, "").trim();

                    const hasMeaningfulLocalContent =
                        localCodeValue.trim().length > 0 ||
                        localTextStripped.length > 0 ||
                        cachedValue.trim().length > 0;

                    const incomingIsEmpty = incoming.trim().length === 0;

                    // 1) If the user typed after this snapshot request started,
                    //    do not let an empty snapshot clobber their edits.
                    if (lastLocalEditAt > requestedAt && incomingIsEmpty) {
                        return;
                    }

                    // 2) If there is already meaningful local content, do not let an empty
                    //    snapshot overwrite it, even if we don't have precise timing.
                    if (hasMeaningfulLocalContent && incomingIsEmpty) {
                        return;
                    }

                    // 3) If mounted editor already has content and incoming differs only because
                    //    snapshot is stale/behind, be conservative when incoming is empty.
                    if (codeEd && localCodeValue.trim().length > 0 && incomingIsEmpty) {
                        return;
                    }

                    if (ted && localTextStripped.length > 0 && incomingIsEmpty) {
                        return;
                    }

                    // Passed validation — now cache it
                    snippetMapRef.current.set(snippetId, incoming);

                    if (incomingUpdatedAt) {
                        lastSeenUpdatedAtRef.current[snippetId] = incomingUpdatedAt;
                    }

                    // ---- MONACO CODE EDITOR ----
                    if (codeEd) {
                        applyingRemoteCodeRef.current.add(snippetId);
                        try {

                            applyRemoteCodeFullReplace(codeEd, incoming);
                            const model = codeEd.getModel();
                            if (model) snippetMapRef.current.set(snippetId, model.getValue());
                        } finally {
                            setTimeout(() => applyingRemoteCodeRef.current.delete(snippetId), 0);
                        }
                        return;
                    }

                    // ---- TIPTAP TEXT EDITOR ----
                    if (ted) {
                        applyingRemoteTipTapRef.current.add(snippetId);
                        try {
                            ted.commands.setContent(incoming as any, { emitUpdate: false });
                            snippetMapRef.current.set(snippetId, ted.getHTML());
                        } catch {
                        } finally {
                            setTimeout(() => applyingRemoteTipTapRef.current.delete(snippetId), 0);
                        }
                        return;
                    }

                    // ---- NO EDITOR MOUNTED YET ----
                    // Only stash pending snapshot if it passed the same empty-content protections.
                    const hasCachedOrDbContent =
                        cachedValue.trim().length > 0 || dbValue.trim().length > 0;

                    if (!(incomingIsEmpty && hasCachedOrDbContent)) {
                        pendingSnapshotRef.current[snippetId] = {
                            text: incoming,
                            updatedAt: incomingUpdatedAt,
                        };
                    }
                }
            );
        },
        [roomId, snippetById]
    );


    const prevOpenSnippetIdsRef = useRef<Set<string>>(new Set());
    const snapshotRequestedRef = useRef<Record<string, boolean>>({});

    useEffect(() => {
        if (!roomId) return;

        const prev = prevOpenSnippetIdsRef.current;
        const current = new Set(openSnippets.map(s => s._id));

        const newlyOpened: string[] = [];
        const newlyClosed: string[] = [];

        // detect newly opened
        current.forEach(id => {
            if (!prev.has(id)) newlyOpened.push(id);
        });

        // detect newly closed
        prev.forEach(id => {
            if (!current.has(id)) newlyClosed.push(id);
        });

        if (newlyOpened.length) {
        }
        if (newlyClosed.length) {
        }

        // → request snapshot on open
        newlyOpened.forEach(id => {
            snapshotRequestedRef.current[id] = true;
            snapshotRequestedAtRef.current[id] = Date.now();
            requestSnippetSnapshot(id);
        });

        // → reset guard on close so re-opening works
        newlyClosed.forEach(id => {
            delete snapshotRequestedRef.current[id];
        });

        prevOpenSnippetIdsRef.current = current;
    }, [roomId, openSnippets, requestSnippetSnapshot]);



    // const changeSnipPermClick = async (id: string, setting: string) => {
    //     if (!canEditSnippet(id)) return
    //     if (activeFile?.resourceType != 'snippet') return;
    //     if (activeFile?.data.snipPermissions === setting) {
    //         return
    //     }

    //     await api.patch(`/snippets/updatesnippet/${roomId}/${id}`, {
    //         snipPermissions: setting
    //     })
    //     setPermissionsPopup(false);
    // };


    const changeSnipPermClick = async (setting: "view" | "edit") => {
        if (!activeSnippetFile) return;

        const id = activeSnippetFile._id;

        if (!canEditSnippet(id)) return;

        if (activeSnippetFile.snipPermissions === setting) {
            setPermissionsPopup(false);
            return;
        }

        await api.patch(`/snippets/updatesnippet/${roomId}/${id}`, {
            snipPermissions: setting,
        });

        setPermissionsPopup(false);
    };



    //end new



    async function copyToClipboard(text: string) {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                return;
            }
        } catch { }
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }



    useEffect(() => {
        const byId = new Map(Snippets.map(s => [String(s._id), s]));

        setOpenSnippets(prev => {
            const next = prev
                .map(os => {
                    const updated = byId.get(String(os._id));
                    return updated ? { ...os, ...updated } : null;
                })
                .filter((s): s is Snippet => s !== null);

            const sameLength = prev.length === next.length;
            const sameItems =
                sameLength &&
                prev.every((item, i) => {
                    const nextItem = next[i];
                    return nextItem && JSON.stringify(item) === JSON.stringify(nextItem);
                });

            return sameItems ? prev : next;
        });
    }, [Snippets, setOpenSnippets]);


    useEffect(() => {
        if (!activeFileId) return;

        const activeStillOpen = openTabs.some((tab) => {
            return (
                tab.resourceType === activeFileId.resourceType &&
                String(tab.data._id) === String(activeFileId.id)
            );
        });

        if (activeStillOpen) return;

        const nextOpen = openTabs[0] ?? null;

        if (nextOpen) {

            if (nextOpen.resourceType === 'snippet') {
                setActiveSnippetId(nextOpen.data._id);
            }

            setActiveFileId({
                id: nextOpen.data._id,
                resourceType: nextOpen.resourceType,
            });
        } else {

            setActiveFileId(null);
            setUiMode("files");
            setSbMode(prev => prev === "editor" ? "filelist" : prev);
            localStorage.setItem("flashroom-ui-mode", "files");
        }
    }, [openTabs, activeFileId, setActiveFileId, setSbMode, setUiMode]);


    // -----

    const copyText = async () => {
        if (!activeSnippet) return;
        const id = activeSnippet._id;

        try {
            let text = '';

            if (activeSnippet.snippetType === 'text') {
                const ed = textEditorsRef.current[id];
                if (!ed) return;
                text = ed.getText();
            } else if (activeSnippet.snippetType === 'code') {
                const ed = codeEditorsRef.current[id];
                if (!ed) return;
                text = ed.getModel()?.getValue() ?? '';
            } else {
                return;
            }

            await copyToClipboard(text);
            showTextCopyNoty(true);
        } catch (err) {
        }
    };

    const downloadActiveResource = async () => {
        try {
            if (roomId && activeFile?.resourceType === "file") {
                await downloadFile(roomId, activeFile.data._id, activeFile.data.name);
                return;
            }

            if (activeFile?.resourceType !== "snippet" || !activeSnippet) return;

            let contents = activeSnippet.snipText;
            let mime = "text/plain;charset=utf-8";
            let ext = "txt";

            if (activeSnippet.snippetType === "text") {
                contents = textEditorsRef.current[activeSnippet._id]?.getText() ?? contents;
            } else {
                const lang = activeSnippet.snippetLanguage || "text";
                contents = codeEditorsRef.current[activeSnippet._id]?.getModel()?.getValue() ?? contents;
                mime = normalizeMime(lang);
                ext = getSnippetExtension(lang);
            }

            await downloadSnippet(`${activeSnippet.snippetName}.${ext}`, contents, mime);
        } catch {
            setDownloadFileErrorPopup(true);
        }
    };

    useEffect(() => {
        if (!textCopyNoty) return;
        const id = setTimeout(() => showTextCopyNoty(false), 2000);
        return () => clearTimeout(id);
    }, [textCopyNoty]);


    const boldClick = () => {
        const ed = activeTextEditor;
        if (!ed) return;
        ed.chain().focus().toggleBold().run();
        setIsBold(ed.isActive('bold'));
    };

    const italicClick = () => {
        const ed = activeTextEditor;
        if (!ed) return;
        ed.chain().focus().toggleItalic().run();
        setIsItalic(ed.isActive('italic'));
    };

    const bulletClick = () => {
        const ed = activeTextEditor;
        if (!ed) return;
        ed.chain().focus().toggleBulletList().run();
        setIsBullet(ed.isActive('bulletList'));
    };


    const getActiveCodeNow = () => {
        const id = activeSnippet?._id;
        if (id == null) return '';
        return (
            codeEditorsRef.current[id]?.getModel()?.getValue() ??
            codeTextRef.current[id] ?? // your mirror cache from onDidChangeContent
            activeCode ?? ''           // fallback to context state
        );
    };


    const wRef = useRef<Worker | null>(null);
    const workerReadyRef = useRef<Promise<void> | null>(null);


    const waitForFlush = async (id: string | null) => {
        if (!id) return;
        const p = flushPromiseRef.current[id];
        if (!p) return;          // nothing pending
        await p;                 // wait for the scheduled flush
    };

    const flushSnippetNow = (snippetId: string | null) => {
        if (!snippetId) return;
        tiptapFlushRef.current[snippetId]?.();
        monacoFlushRef.current[snippetId]?.();
    };


    const monacoFlushRef = useRef<Record<string, (() => void) | null>>({});
    const tiptapFlushRef = useRef<Record<string, (() => void) | null>>({});



    const tabClick = async (
        nextId: string,
        nextResourceType: "snippet" | "file"
    ) => {
        if (activeFile?.resourceType === "snippet" && activeSnippetId) {
            flushSnippetNow(activeSnippetId);
            await waitForFlush(activeSnippetId);
        }

        if (nextResourceType === "snippet") {
            const snippet = Snippets.find(s => s._id === nextId);
            if (!snippet) return;

            setActiveSnippetId(snippet._id);
            setActiveLanguage(snippet.snippetLanguage || "");

            setActiveFileId({
                resourceType: "snippet",
                id: snippet._id,
            });

            return;
        }

        const file = Files.find(f => f._id === nextId);
        if (!file) return;

        setActiveSnippetId(null);
        setActiveLanguage("");

        setActiveFileId({
            resourceType: "file",
            id: file._id,
        });
    };

    useEffect(() => {
        if (globalNewFilePopup || globalNewSnipPopup) {
            setSnipSettingPopup(false)
        }

    }, [globalNewFilePopup, globalNewSnipPopup])


    const closeTabClick = (
        e: React.MouseEvent,
        resourceType: "snippet" | "file",
        id: string
    ) => {
        e.stopPropagation();

        setOpenTabs(prev => {
            const idx = prev.findIndex(
                tab =>
                    tab.resourceType === resourceType &&
                    tab.data._id === id
            );

            if (idx === -1) return prev;

            const next = prev.filter(
                tab =>
                    !(
                        tab.resourceType === resourceType &&
                        tab.data._id === id
                    )
            );

            const closingActive =
                activeFileId?.resourceType === resourceType &&
                activeFileId.id === id;

            if (closingActive) {
                const fallback = next[idx] ?? next[idx - 1] ?? null;

                if (fallback) {
                    setActiveFileId({
                        resourceType: fallback.resourceType,
                        id: fallback.data._id,
                    });

                    if (fallback.resourceType === "snippet") {
                        setActiveSnippetId(fallback.data._id);
                        setActiveLanguage(fallback.data.snippetLanguage || "");
                    } else {
                        setActiveSnippetId(null);
                        setActiveLanguage("");
                    }
                } else {
                    setActiveFileId(null);
                    setActiveSnippetId(null);
                    setActiveLanguage("");
                    setUiMode("files");
                    setSbMode(prev => prev === "editor" ? "filelist" : prev);
                    localStorage.setItem("flashroom-ui-mode", "files");
                }
            }

            return next;
        });

        if (resourceType === "snippet") {
            setOpenSnippets(prev => prev.filter(snippet => snippet._id !== id));
        }
    };


    useEffect(() => {
        setPermissionsPopup(false);
    }, [roomId, activeSnippetId]);





    const canRun = !!activeSnippet &&
        activeSnippet.snippetType !== 'text' &&
        activeSnippet.snippetLanguage !== 'css';


    const isText = activeFile?.resourceType === 'snippet' && activeFile.data.snippetType === 'text'

    const tabsRef = useRef<HTMLDivElement | null>(null);
    const tabsTrackRef = useRef<HTMLDivElement | null>(null);
    const [tabsScroll, setTabsScroll] = useState({ canScroll: false, thumbLeft: 0, thumbWidth: 40 });

    const updateTabsScroll = useCallback(() => {
        const tabsEl = tabsRef.current;
        if (!tabsEl) return;

        const maxScroll = tabsEl.scrollWidth - tabsEl.clientWidth;
        const canScrollTabs = maxScroll > 1;
        const thumbWidth = canScrollTabs
            ? Math.min(300, Math.max(56, (tabsEl.clientWidth / tabsEl.scrollWidth) * tabsEl.clientWidth))
            : tabsEl.clientWidth;
        const thumbTravel = Math.max(0, tabsEl.clientWidth - thumbWidth);
        const thumbLeft = canScrollTabs ? (tabsEl.scrollLeft / maxScroll) * thumbTravel : 0;

        setTabsScroll({
            canScroll: canScrollTabs,
            thumbLeft,
            thumbWidth,
        });
    }, []);

    useEffect(() => {
        updateTabsScroll();

        const tabsEl = tabsRef.current;
        if (!tabsEl) return;

        tabsEl.addEventListener('scroll', updateTabsScroll, { passive: true });
        window.addEventListener('resize', updateTabsScroll);

        const resizeObserver = new ResizeObserver(updateTabsScroll);
        resizeObserver.observe(tabsEl);

        return () => {
            tabsEl.removeEventListener('scroll', updateTabsScroll);
            window.removeEventListener('resize', updateTabsScroll);
            resizeObserver.disconnect();
        };
    }, [openTabs.length, updateTabsScroll]);

    const scrollTabsFromTrack = (clientX: number) => {
        const tabsEl = tabsRef.current;
        const trackEl = tabsTrackRef.current;
        if (!tabsEl || !trackEl) return;

        const trackRect = trackEl.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (clientX - trackRect.left) / trackRect.width));
        tabsEl.scrollLeft = (tabsEl.scrollWidth - tabsEl.clientWidth) * ratio;
        updateTabsScroll();
    };

    const tabsThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const tabsEl = tabsRef.current;
        if (!tabsEl) return;

        const startX = event.clientX;
        const startScrollLeft = tabsEl.scrollLeft;
        const maxScroll = tabsEl.scrollWidth - tabsEl.clientWidth;
        const thumbTravel = Math.max(1, tabsEl.clientWidth - tabsScroll.thumbWidth);

        const handlePointerMove = (moveEvent: PointerEvent) => {
            const nextScroll = startScrollLeft + ((moveEvent.clientX - startX) / thumbTravel) * maxScroll;
            tabsEl.scrollLeft = Math.min(maxScroll, Math.max(0, nextScroll));
            updateTabsScroll();
        };

        const handlePointerUp = () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
    };

    // ---- Tab reorder (drag to slide, VS Code style) ----
    const TAB_GAP = 5;

    type TabOrderItem = { key: string; left: number; width: number; center: number };

    const tabElRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const dragJustEndedRef = useRef(false);

    const tabDragRef = useRef<{
        key: string;
        pointerId: number;
        startX: number;
        originalLeft: number;
        width: number;
        order: TabOrderItem[];
        originalIndex: number;
        containerMaxLeft: number;
        moved: boolean;
        currentNewPos: number;
    } | null>(null);

    const registerTabEl = (key: string) => (el: HTMLDivElement | null) => {
        if (el) tabElRefs.current.set(key, el);
        else tabElRefs.current.delete(key);
    };

    const handleTabPointerDown = (e: React.PointerEvent<HTMLDivElement>, tabKey: string) => {
        if (e.button !== 0) return;
        if ((e.target as HTMLElement).closest('.x-icon-tab')) return;

        const tabsEl = tabsRef.current;
        if (!tabsEl) return;

        const order: TabOrderItem[] = openTabs.map((t) => {
            const key = `${t.resourceType}-${t.data._id}`;
            const el = tabElRefs.current.get(key);
            const left = el?.offsetLeft ?? 0;
            const width = el?.offsetWidth ?? 0;
            return { key, left, width, center: left + width / 2 };
        });

        const originalIndex = order.findIndex((it) => it.key === tabKey);
        if (originalIndex === -1) return;

        const self = order[originalIndex];

        tabDragRef.current = {
            key: tabKey,
            pointerId: e.pointerId,
            startX: e.clientX,
            originalLeft: self.left,
            width: self.width,
            order,
            originalIndex,
            containerMaxLeft: Math.max(0, tabsEl.scrollWidth - self.width),
            moved: false,
            currentNewPos: originalIndex,
        };

        try { (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId); } catch { }
    };

    const handleTabPointerMove = (e: React.PointerEvent<HTMLDivElement>, tabKey: string) => {
        const drag = tabDragRef.current;
        if (!drag || drag.key !== tabKey) return;

        const rawDeltaX = e.clientX - drag.startX;

        if (!drag.moved) {
            if (Math.abs(rawDeltaX) < 4) return;
            drag.moved = true;
            tabsRef.current?.classList.add('tabs-reordering');
        }

        let nextLeft = drag.originalLeft + rawDeltaX;
        nextLeft = Math.min(drag.containerMaxLeft, Math.max(0, nextLeft));
        const deltaX = nextLeft - drag.originalLeft;

        const draggedEl = tabElRefs.current.get(tabKey);
        if (draggedEl) {
            draggedEl.style.transition = 'none';
            draggedEl.style.zIndex = '5';
            draggedEl.style.transform = `translate3d(${deltaX}px, 0, 0)`;
            draggedEl.classList.add('tab-dragging');
        }

        // Use the leading edge in the direction of travel (not the dragged tab's
        // center) as the trigger point, so a neighbor swaps as soon as that edge
        // crosses the neighbor's midpoint — about half the drag distance that
        // comparing centers would require.
        const leadingEdge =
            deltaX > 0 ? nextLeft + drag.width :
                deltaX < 0 ? nextLeft :
                    nextLeft + drag.width / 2;

        let newPos = 0;
        for (const item of drag.order) {
            if (item.key === tabKey) continue;
            if (item.center < leadingEdge) newPos++;
            else break;
        }
        drag.currentNewPos = newPos;

        drag.order.forEach((item, idx) => {
            if (item.key === tabKey) return;
            const el = tabElRefs.current.get(item.key);
            if (!el) return;

            const amongOthersIdx = idx < drag.originalIndex ? idx : idx - 1;
            const finalIdx = amongOthersIdx < newPos ? amongOthersIdx : amongOthersIdx + 1;
            const shift = finalIdx - idx;

            el.style.transition = 'transform 140ms ease';
            el.style.transform = shift === 0 ? '' : `translate3d(${shift * (drag.width + TAB_GAP)}px, 0, 0)`;
        });
    };

    const handleTabPointerUp = (e: React.PointerEvent<HTMLDivElement>, tabKey: string) => {
        const drag = tabDragRef.current;
        if (!drag || drag.key !== tabKey) return;

        try { (e.currentTarget as HTMLDivElement).releasePointerCapture(drag.pointerId); } catch { }

        tabsRef.current?.classList.remove('tabs-reordering');

        drag.order.forEach((item) => {
            const el = tabElRefs.current.get(item.key);
            if (!el) return;
            el.style.transition = '';
            el.style.transform = '';
            el.style.zIndex = '';
            el.classList.remove('tab-dragging');
        });

        const moved = drag.moved;
        const newPos = drag.currentNewPos;
        const fromIndex = drag.originalIndex;
        tabDragRef.current = null;

        if (!moved) return;

        dragJustEndedRef.current = true;

        if (newPos === fromIndex) return;

        setOpenTabs((prev) => {
            const idx = prev.findIndex((t) => `${t.resourceType}-${t.data._id}` === tabKey);
            if (idx === -1) return prev;
            const next = [...prev];
            const [movedTab] = next.splice(idx, 1);
            next.splice(newPos, 0, movedTab);
            return next;
        });
    };

    const permissionsBlock = (
        <div className="permissions-anchor">
            {permissionsPopup && activeSnippetFile ? (
                <div className="fade-in" ref={permPopupRef} id="permissions-popup">
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "2rem",
                        }}
                    >
                        <h4>Edit Permissions</h4>

                        <div id="x-wrap" onClick={(e) => togglePermissionsPopup(e)}>
                            <svg className="x-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 111.01 111.01">
                                <g id="Layer_3" data-name="Layer 3">
                                    <path
                                        className="cls-1"
                                        d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                                        transform="translate(-124.17 -181.62)"
                                    />
                                </g>
                            </svg>
                        </div>
                    </div>

                    <div id="edit-perm-btn">
                        <button
                            onClick={() => changeSnipPermClick("view")}
                            className={`change-perm-btn edpb-l ${activeSnippetFile.snipPermissions === "view"
                                ? "active-snip-perm-btn"
                                : ""
                                }`}
                        >
                            Read Only
                        </button>

                        <button
                            onClick={() => changeSnipPermClick("edit")}
                            className={`change-perm-btn edpb-r ${activeSnippetFile.snipPermissions === "edit"
                                ? "active-snip-perm-btn"
                                : ""
                                }`}
                        >
                            Open
                        </button>
                    </div>
                </div>
            ) : null}

            {activeSnippetFile && (
                <button
                    className="permissions"
                    disabled={!canChangeSnipSettings(activeSnippetFile._id)}
                    onPointerDownCapture={(e) => e.stopPropagation()}
                    onMouseDownCapture={(e) => e.stopPropagation()}
                    onClick={(e) => togglePermissionsPopup(e)}
                >
                    <img
                        draggable={false}
                        src={`${process.env.PUBLIC_URL}/images/${ctheme === "dark" ? "lock" : "lock-2"
                            }.png`}
                        alt="lock icon"
                        height="20px"
                    />

                    <span>
                        {activeSnippetFile.snipPermissions === "view" ? "Read Only" : "Open"}
                    </span>
                </button>
            )}
        </div>
    );

    return (
        <div className={`editor-positioning`}>
            {
                !Snippets.length && !Files.length ? (
                    <div id='no-snips'>
                        <div className='empty-editor-panel'>
                            <h2>There are no files in this room</h2>

                            <p>Create a file or upload one to get started</p>
                        </div>


                    </div>

                ) : !openTabs.length ? (
                    <div id='no-open-snips'>
                        <div className='empty-editor-panel'>
                            <h2>Open a file to get started</h2>
                            <p>Select a file from the sidebar to bring it into the editor.</p>
                        </div>
                    </div>

                ) : (
                    <div className={`editor-wrapper ${activeTerminal ? 'editor-wrapper-to' : 'editor-wrapper-tc'}`}>

                        {snipSettingPopup && activeSnippet && (
                            <div ref={popupRef}>
                                <SnipSettingPopup
                                    initialState={activeSnippet.snippetName}
                                    initialPerm={activeSnippet.snipPermissions}
                                    onClose={snipSettingCancelClick}
                                    viewerPerm={canChangeSnipSettings(activeSnippet._id) ? "editor" : "viewer"}
                                />
                            </div>
                        )}


                        {fileSettingPopup &&
                            <div ref={filePopupRef}>
                                <FileSettingPopup onClose={() => setFileSettingPopup(false)} />
                            </div>
                        }

                        <div className="toolbar">
                            <div className={`tabs-shell ${tabsScroll.canScroll ? 'tabs-shell-overflow' : ''}`}>
                                <div className='tabs' ref={tabsRef}>

                                    {openTabs.map((tab) => {
                                        const isActive =
                                            activeFileId?.resourceType === tab.resourceType &&
                                            activeFileId?.id === tab.data._id;

                                        const tabKey = `${tab.resourceType}-${tab.data._id}`;

                                        const tabName = getTabDisplayName(tab);

                                        // Only disambiguate when another open tab currently
                                        // shows this same name — mirrors VS Code, which leaves
                                        // uniquely-named tabs alone.
                                        const isDuplicateTabName = (tabNameCounts.get(tabName) ?? 0) > 1;
                                        const parentFolder = tab.data.folderParent
                                            ? findFolderInTree(Folders, tab.data.folderParent)
                                            : null;
                                        const folderLabel = isDuplicateTabName
                                            ? parentFolder?.fname ?? roomDetails?.roomName ?? "Room"
                                            : null;
                                        const folderLabelIcon = !parentFolder
                                            ? `${process.env.PUBLIC_URL}/images/${ctheme === "light" ? "room-icon" : "room-icon-white"}.png`
                                            : `${process.env.PUBLIC_URL}/images/folder-closed-dark.png`;

                                        const iconSrc =
                                            tab.resourceType === "snippet"
                                                ? `${process.env.PUBLIC_URL}/images/${getSnippetIcon(tab.data.snippetLanguage || "txt")}.png`
                                                : `${process.env.PUBLIC_URL}/images/${getFileRefIcon(tab.data.name || "txt")}`;

                                        const iconAlt =
                                            tab.resourceType === "snippet"
                                                ? tab.data.snippetLanguage || "txt"
                                                : tab.data.fileExtension || "file";

                                        return (
                                            <div
                                                className={`tab ${isActive ? "active-snippet-tab" : ""}`}
                                                key={tabKey}
                                                ref={registerTabEl(tabKey)}
                                                onClick={() => {
                                                    if (dragJustEndedRef.current) {
                                                        dragJustEndedRef.current = false;
                                                        return;
                                                    }
                                                    tabClick(tab.data._id, tab.resourceType);
                                                }}
                                                onPointerDown={(e) => handleTabPointerDown(e, tabKey)}
                                                onPointerMove={(e) => handleTabPointerMove(e, tabKey)}
                                                onPointerUp={(e) => handleTabPointerUp(e, tabKey)}
                                                onPointerCancel={(e) => handleTabPointerUp(e, tabKey)}
                                            >
                                                <img draggable={false} src={iconSrc} alt={iconAlt} />

                                                <span className='snip-tab-name'>{tabName}</span>

                                                {folderLabel && (
                                                    <span className='snip-tab-dupe-badge' title={`In ${folderLabel}`}>
                                                        <img draggable={false} className='snip-tab-dupe-badge-icon' src={folderLabelIcon} alt="" />
                                                        <span className='snip-tab-dupe-badge-label'>{folderLabel}</span>
                                                    </span>
                                                )}

                                                <svg
                                                    className="x-icon x-icon-tab"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 111.01 111.01"
                                                    onClick={(e) => closeTabClick(e, tab.resourceType, tab.data._id)}
                                                >
                                                    <g id="Layer_3" data-name="Layer 3">
                                                        <path
                                                            className="cls-1"
                                                            d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                                                            transform="translate(-124.17 -181.62)"
                                                        />
                                                    </g>
                                                </svg>
                                            </div>
                                        );
                                    })}


                                </div>

                                {tabsScroll.canScroll && (
                                    <div
                                        className='tabs-scroll-widget'
                                        ref={tabsTrackRef}
                                        onPointerDown={(event) => scrollTabsFromTrack(event.clientX)}
                                        aria-hidden='true'
                                    >
                                        <div
                                            className='tabs-scroll-thumb'
                                            style={{
                                                width: `${tabsScroll.thumbWidth}px`,
                                                transform: `translateX(${tabsScroll.thumbLeft}px)`,
                                            }}
                                            onPointerDown={tabsThumbPointerDown}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className='toolbar-lower'>
                                {isMobileToolbar && permissionsBlock}

                                <div className='copy-and-options'>


                                    <div
                                        onClick={downloadActiveResource} className='clip-outer quick-flex sc-db'
                                        aria-label="Download File"
                                        title='Download'
                                    >
                                        <Download />
                                    </div>

                                    {
                                        activeFileId?.resourceType === "snippet" && (
                                            <div onClick={copyText} className='clip-outer quick-flex'
                                                aria-label='Copy'
                                                title='Copy'
                                            >
                                                <img draggable={false} className='copy-icon-img' src={`${process.env.PUBLIC_URL}/images/copy.png`} alt='copy text icon' />

                                            </div>
                                        )
                                    }

                                    {/* <div className='sc-spacer' /> */}





                                    {/* {activeTerminal &&
                                        <button className='quick-flex run-btn-to run-code-btn'
                                            onClick={runTermClick}

                                        >

                                            <img src={`${process.env.PUBLIC_URL}/images/run.png`} width='15px' alt='run code icon' />
                                            <span>Run</span>
                                        </button>} */}



                                    {/* <div className='settings-icon' onClick={activeTab?.resourceType === 'snippet' ? snipSettingClick : fileSettingClick}>
                                        <div />
                                        <div />
                                        <div />
                                    </div> */}

                                    <div className='clip-outer new-setting-btn'
                                        onClick={activeTab?.resourceType === 'snippet' ? snipSettingClick : fileSettingClick}

                                        aria-label='File Settings'
                                        title='File Settings'
                                    >
                                        <OptionsIcon />
                                    </div>




                                </div>
                            </div>
                        </div>

                        {/* here before */}

                        <div id='snippet-holder'>



                            {textCopyNoty && <div className='copy-noty'>Copied to Clipboard</div>}

                            {
                                stableOrderedTabs.map((fil, index) => (

                                    <div
                                        key={fil.data._id}
                                        className={`snippet ${activeFileId?.id === fil.data._id ? 'active-snippet' : 'inactive-snippet'}`}              >


                                        {
                                            fil.resourceType === 'snippet' ? (
                                                <EditorErrorBoundary resetKey={activeFileId?.id ?? ''}>
                                                    <Snippet
                                                        id={fil.data._id}
                                                        isActive={activeFileId?.id === fil.data._id}
                                                        fileName={fil.data.snippetName}
                                                        fileType={fil.data.snippetType}
                                                        fileLanguage={fil.data.snippetLanguage || ''}
                                                        onUpdate={(payload) => handleEditorUpdate(payload, fil.data._id)}
                                                        readOnly={!canEditSnippet(fil.data._id)}

                                                    />
                                                </EditorErrorBoundary>
                                            ) : (
                                                <FilePreview
                                                    id={fil.data._id}
                                                    name={fil.data.name}
                                                    mime={fil.data.mime || ""}
                                                    fileExtension={fil.data.fileExtension || ""}

                                                />
                                            )
                                        }

                                    </div>

                                ))
                            }
                        </div>


                        <div id='editor-bottom'>

                            {!isMobileToolbar && permissionsBlock}

                            {isText &&
                                <div className='tb-btns'>
                                    <button className={`bold-btn ${isBold ? 'active-btn' : ''}`} onClick={boldClick}>B</button>
                                    <button className={`italic-btn ${isItalic ? 'active-btn' : ''}`} onClick={italicClick}>I</button>
                                    <button className={`list-btn center ${isBullet ? 'active-btn' : ''}`} onClick={bulletClick}>
                                        <Bulletlist />
                                    </button>
                                </div>
                            }


                            {/* {canRun &&
                                <button className='quick-flex run-code-btn'
                                    onClick={runTermClick}

                                >

                                    <img src={`${process.env.PUBLIC_URL}/images/run.png`} width='15px' />
                                    <span>Run</span>
                                </button>
                            } */}


                            {/* {isActiveAudioFile && (
                                <div id="media-player-slot" />
                            )} */}

                            <div
                                id="media-player-slot"
                                className={`media-player-slot ${isActiveAudioFile ? "activemps" : "inactivemps"}`}
                            />

                            <div id='video-player-slot'
                                className={`video-player-slot ${isActiveVideoFile ? "activevp" : "inactive-vp"}`}
                            />




                            {iconFile && (
                                <img
                                    draggable={false}
                                    className='sc-b-img'
                                    src={`${process.env.PUBLIC_URL}/images/${iconFile}`}
                                />
                            )}





                        </div>
                    </div>
                )
            }

        </div >
    );
};



export default SnippetContainer;
